import React, { FC } from 'react';
import { FlexCol, Loader } from '@minecraft.atoms';
import { useTranslation } from '@blocs.i18n';
import { WindowWidthContext } from '@minecraft.utils';
import { DiscountTypeEnum } from '@minecraft.graphql-operations';
import { LoadingSkeleton } from '@minecraft.loading-skeleton';
import { RegistrationCheckoutFormProps } from './types';
import { Purchase } from '../types';
import { PaymentForm } from './PaymentForm';
import { priceFormatter } from '../helpers/priceFormatter';
import { BillingCodes } from '../enums/plans';
import { MobileRegistrationUpsell } from './MobileRegistrationUpsell';

export const RegistrationCheckoutForm: FC<RegistrationCheckoutFormProps> = React.memo(
  ({
    addPaymentError,
    addPaymentLoading,
    threeDActionContext,
    currencyCode,
    countryCode,
    paywallLocation,
    allowCancel,
    selectedPlan,
    currentPurchase,
    completedPurchase,
    completePurchaseLoading,
    completePurchaseError,
    selectedPaymentOption,
    wallet,
    onCancel,
    isAnnual,
    setIsAnnual,
    disableTrial,
    onApplyCoupon,
    couponLoading,
    applyCouponError,
    onRemoveCoupon,
    showApplePayButton,
    onClickApplePay,
    onSubmit,
    onAddCardDuringCheckout,
    onPayPalToken,
    handlePaymentPriorityChange,
    paymentMethodFormRef,
    paymentMethodFormikRef,
    setShowAddPaymentMethod,
    showAddPaymentMethod,
    discountType,
    allowPurchase,
  }) => {
    const { t } = useTranslation();
    const isMobile = WindowWidthContext.useIsMobile();
    const handleSetIsAnnual = () => setIsAnnual(!isAnnual);
    const price = priceFormatter({
      price: Number(selectedPlan?.price),
      currencyCode: selectedPlan?.currencyCode,
      countryCode,
      t,
    });

    const isCardlessTrial = selectedPlan?.isCardlessTrial;
    const isFreePlanSelected = selectedPlan?.billingCode === BillingCodes.registration;
    const isReppedRegDiscount = discountType === DiscountTypeEnum.ReppedRegistrationDiscount;

    return (
      <Loader
        loading={!selectedPlan}
        LoaderComponent={
          <FlexCol alignItems="stretch" w="full" data-testid="mobile-registration-loader" gap="2">
            <LoadingSkeleton width="100%" height="42px" />
            <LoadingSkeleton width="100%" height="500px" />
          </FlexCol>
        }
      >
        <FlexCol w="full" justifyContent="start" alignItems="start" gap="4">
          {!isFreePlanSelected && !isReppedRegDiscount && isMobile && (
            <MobileRegistrationUpsell
              isAnnual={isAnnual}
              selectedPlan={selectedPlan}
              handleSetIsAnnual={handleSetIsAnnual}
              price={price}
              disableTrial={disableTrial}
            />
          )}
          <PaymentForm
            mode="checkout"
            addPaymentError={addPaymentError}
            addPaymentLoading={addPaymentLoading}
            allowCancel={allowCancel}
            completedPurchase={completedPurchase}
            completePurchaseError={completePurchaseError}
            completePurchaseLoading={completePurchaseLoading}
            countryCode={countryCode}
            currencyCode={currencyCode}
            currentPurchase={currentPurchase as Purchase}
            onCancel={onCancel}
            paywallLocation={paywallLocation}
            selectedPaymentOption={selectedPaymentOption}
            selectedPlan={selectedPlan}
            threeDActionContext={threeDActionContext}
            wallet={wallet}
            onApplyCoupon={onApplyCoupon}
            couponLoading={couponLoading}
            applyCouponError={applyCouponError}
            onRemoveCoupon={onRemoveCoupon}
            showApplePayButton={showApplePayButton}
            onClickApplePay={onClickApplePay}
            onSubmit={onSubmit}
            onAddCardDuringCheckout={onAddCardDuringCheckout}
            onPayPalToken={onPayPalToken}
            handlePaymentPriorityChange={handlePaymentPriorityChange}
            paymentMethodFormRef={paymentMethodFormRef}
            paymentMethodFormikRef={paymentMethodFormikRef}
            setShowAddPaymentMethod={setShowAddPaymentMethod}
            showAddPaymentMethod={showAddPaymentMethod}
            allowPurchase={allowPurchase}
            showFormBorder={!isCardlessTrial && !isFreePlanSelected}
          />
        </FlexCol>
      </Loader>
    );
  }
);
